import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserLayout from "../../components/user/dashboard/UserLayout";
import { MyRentalsSkeleton } from "../../components/user/dashboard/MyRentalsSkeleton";
import {
  Search,
  Download,
  Eye,
  XCircle,
  Calendar,
  Package,
  Loader2,
  AlertCircle
} from "lucide-react";
import { useUserRentals } from "../../services/user/UserRentalsService";
import { useNotification } from "../../context/NotificationContext";

export default function MyRentals() {
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedCancelRental, setSelectedCancelRental] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const { rentals, loading, pagination, fetchRentals, cancelRental, downloadRental } = useUserRentals();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchRentals(1, activeTab);
  }, [fetchRentals, activeTab]);

  const tabs = ["All", "Generated", "Approved", "In Use", "Completed", "Cancelled"];

  const handleCancelClick = (rental) => {
    setSelectedCancelRental(rental);
    setIsCancelModalOpen(true);
  };

  const confirmCancelRental = async () => {
    if (!selectedCancelRental) return;
    setActionLoadingId(`cancel-${selectedCancelRental.id}`);
    const result = await cancelRental(selectedCancelRental.id);
    setActionLoadingId(null);
    if (result.success) {
      setIsCancelModalOpen(false);
      setSelectedCancelRental(null);
      showNotification({
        type: "success",
        title: "Invoice Cancelled",
        message: `Invoice ${selectedCancelRental.invoice_number} has been cancelled.`
      });
      fetchRentals(pagination.page, activeTab);
    } else {
      showNotification({
        type: "failed",
        title: "Cancellation Failed",
        message: result.error || "Failed to cancel invoice. Please try again."
      });
    }
  };

  const handleDownload = async (rental) => {
    setActionLoadingId(`download-${rental.id}`);
    try {
      const response = await downloadRental(rental.id);
      const blob = response.data;

      if (!blob || blob.size === 0) {
        showNotification({
          type: "info",
          title: "File Unavailable",
          message: "The invoice PDF could not be found. It may still be generating."
        });
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `St-Stephens-Rentals-${rental.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: "success",
        title: "Download Started",
        message: "Your invoice is being downloaded."
      });
    } catch (error) {
      console.error("Download error:", error);
      showNotification({
        type: "failed",
        title: "Download Failed",
        message: "Failed to download the invoice. Please try again."
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredRentals = rentals.filter(rental =>
    rental.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <UserLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1400px] mx-auto space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-medium text-[#111111] dark:text-white font-serif">My Rentals</h1>
            <p className="text-[#666666] dark:text-[#A0A0A0] mt-1">Manage and track your Rentals.</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${activeTab === tab
                  ? "bg-[#62397f] text-white shadow-lg"
                  : "text-[#666666] dark:text-[#A0A0A0] hover:bg-gray-100 dark:hover:bg-[#262626]"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-72">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]" />
            <input
              type="text"
              placeholder="Search by Invoice number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50"
            />
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm overflow-x-auto min-h-[400px]">
          {loading && rentals.length === 0 ? (
            <MyRentalsSkeleton />
          ) : filteredRentals.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <AlertCircle className="text-[#999999]" size={32} />
              <p className="text-[#666666] dark:text-[#A0A0A0] text-sm font-medium">No rentals found.</p>
            </div>
          ) : (
            <table className="w-full text-left min-w-[900px]">
              <thead>
                <tr className="bg-[#F9FAFB] dark:bg-[#0F0F0F] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider">Invoice ID</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider">No. of Items</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider">Total</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0F0] dark:divide-[#2A2A2A]">
                {filteredRentals.map((rental) => (
                  <tr key={rental.id} className="hover:bg-gray-50 dark:hover:bg-[#212121] transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span onClick={() => navigate(`/dashboard/rentals/${rental.id}`)} className="text-sm font-medium text-[#62397f] dark:text-[#A78BFA] font-serif cursor-pointer hover:underline">{rental.invoice_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-[#62397f]" />
                        <span className="text-sm font-semibold text-[#333333] dark:text-[#E0E0E0]">
                          {rental.items_count} {rental.items_count === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-[#111111] dark:text-white font-medium">
                          {new Date(rental.rental_start).toLocaleDateString()} - {new Date(rental.rental_end).toLocaleDateString()}
                        </span>
                        {rental.overdue && (
                          <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight">Overdue</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#111111] dark:text-white font-serif">₦{rental.total.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm border ${rental.status === 'approved' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                        rental.status === 'generated' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                          rental.status === 'completed' ? 'bg-green-50 text-green-600 border-green-100' :
                            rental.status === 'in_use' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' :
                              'bg-gray-50 text-gray-600 border-gray-100'
                        }`}>
                        {rental.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => navigate(`/dashboard/rentals/${rental.id}`)}
                          disabled={!!actionLoadingId}
                          className="p-1.5 hover:bg-[#62397f] hover:text-white rounded-lg text-[#666666] dark:text-[#A0A0A0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownload(rental)}
                          disabled={!!actionLoadingId}
                          className="p-1.5 hover:bg-[#62397f] hover:text-white rounded-lg text-[#666666] dark:text-[#A0A0A0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download PDF"
                        >
                          {actionLoadingId === `download-${rental.id}` ? (
                            <Loader2 size={18} className="animate-spin text-[#62397f]" />
                          ) : (
                            <Download size={18} />
                          )}
                        </button>
                        {rental.status === 'generated' && (
                          <button
                            onClick={() => handleCancelClick(rental)}
                            disabled={!!actionLoadingId}
                            className="p-1.5 hover:bg-red-500 hover:text-white rounded-lg text-[#666666] dark:text-[#A0A0A0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Cancel Invoice"
                          >
                            {actionLoadingId === `cancel-${rental.id}` ? (
                              <Loader2 size={18} className="animate-spin text-red-500" />
                            ) : (
                              <XCircle size={18} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {[...Array(pagination.pages)].map((_, i) => (
              <button
                key={i}
                onClick={() => fetchRentals(i + 1, activeTab)}
                className={`w-10 h-10 rounded-xl font-bold transition-all ${pagination.page === i + 1
                  ? "bg-[#62397f] text-white shadow-lg"
                  : "bg-white dark:bg-[#1A1A1A] text-[#666666] border border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-gray-50"
                  }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* Mobile List View */}
        <div className="md:hidden space-y-4">
          {loading && rentals.length === 0 ? (
            <MyRentalsSkeleton />
          ) : filteredRentals.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
              <AlertCircle className="text-[#999999]" size={32} />
              <p className="text-[#666666] dark:text-[#A0A0A0] text-sm font-medium">No rentals found.</p>
            </div>
          ) : (
            filteredRentals.map((rental) => (
              <div key={rental.id} className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <span onClick={() => navigate(`/dashboard/rentals/${rental.id}`)} className="text-xs font-bold text-[#62397f] uppercase cursor-pointer hover:underline">{rental.invoice_number}</span>
                    <p className="text-sm font-bold text-[#111111] dark:text-white leading-snug">
                      {rental.items_count} {rental.items_count === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider ${rental.status === 'approved' ? 'bg-blue-50 text-blue-600' :
                    rental.status === 'generated' ? 'bg-amber-50 text-amber-600' :
                      rental.status === 'completed' ? 'bg-green-50 text-green-600' :
                        'bg-gray-50 text-gray-600'
                    }`}>
                    {rental.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-[#F0F0F0] dark:border-[#2A2A2A]">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-[#666666]" />
                    <span className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium">
                      {new Date(rental.rental_start).toLocaleDateString()}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-[#111111] dark:text-white">₦{rental.total.toLocaleString()}</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate(`/dashboard/rentals/${rental.id}`)}
                    disabled={!!actionLoadingId}
                    className="flex items-center justify-center gap-2 py-3 bg-[#F9FAFB] dark:bg-[#262626] rounded-xl text-xs font-bold text-[#333] dark:text-white hover:bg-[#F5F3FF] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye size={14} />
                    <span>View</span>
                  </button>
                  {rental.status === 'generated' && (
                    <button
                      onClick={() => handleCancelClick(rental)}
                      disabled={!!actionLoadingId}
                      className="flex items-center justify-center gap-2 py-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoadingId === `cancel-${rental.id}` ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <XCircle size={14} />
                      )}
                      <span>Cancel</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload(rental)}
                    disabled={!!actionLoadingId}
                    className={`flex items-center justify-center gap-2 py-3 bg-[#62397f] text-white rounded-xl text-xs font-bold hover:bg-[#7C3AED] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${rental.status !== 'generated' ? 'col-span-2' : ''}`}
                  >
                    {actionLoadingId === `download-${rental.id}` ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    <span>Invoice</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <ConfirmCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => {
            setIsCancelModalOpen(false);
            setSelectedCancelRental(null);
          }}
          onConfirm={confirmCancelRental}
          invoiceNumber={selectedCancelRental?.invoice_number}
          isLoading={loading}
        />
      </div>
      <style jsx="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </UserLayout>
  );
}

// Custom Confirm Cancel Modal
const ConfirmCancelModal = ({ isOpen, onClose, onConfirm, invoiceNumber, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-2xl p-6 lg:p-8 transform transition-all animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center text-red-500 mb-6 border border-red-100 dark:border-red-900/20">
            <AlertCircle size={32} />
          </div>

          <h3 className="text-xl font-medium text-[#111111] dark:text-white mb-2 font-serif">
            Cancel Invoice?
          </h3>
          <p className="text-[#6B7280] dark:text-[#A0A0A0] text-sm leading-relaxed mb-8">
            Are you sure you want to cancel Invoice <strong>{invoiceNumber}</strong>? This action is permanent and cannot be undone.
          </p>

          <div className="grid grid-cols-2 gap-3 w-full">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#4B5563] dark:text-[#A0A0A0] rounded-xl text-sm font-semibold hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors disabled:opacity-50"
            >
              No, Keep It
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-red-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Yes, Cancel"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
